import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Base — Scribane Design System spec:
  // font: Inter Medium 14px · radius 10px · height 44px · padding 0 20px
  // transition: background + color 220ms · focus: 3px corrector-green ring @ 40%
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-medium no-underline hover:no-underline transition-[background-color,color] duration-[220ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(94,204,128,0.4)] disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // primary — verde corrector bg · hover inverts to grafito
        default:
          "bg-[var(--scribane-green)] text-[#272729] hover:bg-[#272729] hover:text-[var(--scribane-green)]",
        primary:
          "bg-[var(--scribane-green)] text-[#272729] hover:bg-[#272729] hover:text-[var(--scribane-green)]",
        // inverse — grafito bg (stays) · white text · hover text turns corrector green
        // dark theme flips the base colors: white bg · grafito text
        inverse:
          "bg-[var(--scribane-black)] text-white font-semibold hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)] dark:bg-white dark:text-[var(--scribane-black)] dark:hover:bg-white dark:hover:text-[var(--scribane-green)]",
        // secondary (outline) — white bg · grafito-40 border · hover grafito-10
        outline:
          "bg-white text-[#272729] border border-[rgba(39,39,41,0.4)] hover:bg-[#E9E9EA] hover:border-[#272729] dark:bg-transparent dark:text-foreground dark:border-input dark:hover:bg-accent dark:hover:text-accent-foreground dark:hover:border-input",
        // ghost — no bg · hover accent
        ghost:
          "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground",
        // destructive — kept for internal app use
        destructive:
          "bg-destructive text-destructive-foreground transition-[background-color,color,filter] hover:brightness-90 dark:hover:brightness-125",
        // secondary alias kept for existing app usage
        secondary:
          "bg-white text-[#272729] border border-[rgba(39,39,41,0.4)] hover:bg-[#E9E9EA] hover:border-[#272729] dark:bg-transparent dark:text-foreground dark:border-input dark:hover:bg-accent dark:hover:text-accent-foreground dark:hover:border-input",
        link:
          "bg-transparent text-foreground px-0",
      },
      size: {
        default: "h-[44px] px-5",
        sm: "h-8 rounded-md px-3 text-sm",
        lg: "h-[44px] px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  dialogPrimary?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, dialogPrimary, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const resolvedSize = size ?? (dialogPrimary ? "sm" : "default")
    return (
      <Comp
        className={cn(buttonVariants({ variant, size: resolvedSize, className }))}
        ref={ref}
        {...(dialogPrimary ? { "data-dialog-primary": true } : {})}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
